import { useCallback } from "react";
import { useAuth } from "./useAuth";

export const useDrawings = () => {
  const API_URL = import.meta.env.VITE_APP_API_URL || "/api";
  const getToken = () => localStorage.getItem("auth_token");

  const saveDrawing = useCallback(
    async (drawing: { id?: number; name?: string; data: any }) => {
      const token = getToken();
      if (!token) {
        throw new Error("Authentication required");
      }

      const response = drawing.id
        ? await fetch(`${API_URL}/drawings/${drawing.id}`, {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(drawing),
          })
        : await fetch(`${API_URL}/drawings`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(drawing),
          });

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || "Failed to save drawing");
      }
      return result;
    },
    [API_URL]
  );

  const loadDrawings = useCallback(async () => {
    const token = getToken();
    if (!token) {
      throw new Error("Authentication required");
    }

    const response = await fetch(`${API_URL}/drawings`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    const result = await response.json();
    if (!response.ok) {
      throw new Error(result.error || "Failed to load drawings");
    }
    return result.drawings;
  }, [API_URL]);

  const loadDrawing = useCallback(
    async (id: number) => {
      const token = getToken();
      if (!token) {
        throw new Error("Authentication required");
      }

      const response = await fetch(`${API_URL}/drawings/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || "Failed to load drawing");
      }
      return result.drawing;
    },
    [API_URL]
  );

  const deleteDrawing = useCallback(
    async (id: number) => {
      const token = getToken();
      if (!token) {
        throw new Error("Authentication required");
      }

      const response = await fetch(`${API_URL}/drawings/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || "Failed to delete drawing");
      }
      return result;
    },
    [API_URL]
  );

  return { saveDrawing, loadDrawings, loadDrawing, deleteDrawing };
};